import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { areasAPI, teamsAPI, cargosAPI, companiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { UserCog, Briefcase, Award, Plus, Trash2, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const TeamsAreasPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [areas, setAreas] = useState([]);
  const [teams, setTeams] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [areaDialog, setAreaDialog] = useState(false);
  const [teamDialog, setTeamDialog] = useState(false);
  const [cargoDialog, setCargoDialog] = useState(false);
  
  const [areaForm, setAreaForm] = useState({ name: '', description: '' });
  const [teamForm, setTeamForm] = useState({ name: '', area_id: '', min_coincidence_days: 1 });
  const [cargoForm, setCargoForm] = useState({ name: '', priority_level: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadData();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      if (user?.role === 'superadmin') {
        const response = await companiesAPI.getAll();
        setCompanies(response.data);
        if (response.data.length > 0) {
          setSelectedCompany(response.data[0].id);
        }
      } else if (user?.company_id) {
        setSelectedCompany(user.company_id);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [areasRes, teamsRes, cargosRes] = await Promise.all([
        areasAPI.getAll(selectedCompany),
        teamsAPI.getAll(),
        cargosAPI.getAll(selectedCompany),
      ]);
      setAreas(areasRes.data);
      setTeams(teamsRes.data);
      setCargos(cargosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleCreateArea = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await areasAPI.create({ ...areaForm, company_id: selectedCompany });
      toast.success('Área creada exitosamente');
      setAreaDialog(false);
      setAreaForm({ name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error('Error al crear área');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await teamsAPI.create(teamForm);
      toast.success('Equipo creado exitosamente');
      setTeamDialog(false);
      setTeamForm({ name: '', area_id: '', min_coincidence_days: 1 });
      loadData();
    } catch (error) {
      toast.error('Error al crear equipo');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCargo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cargosAPI.create({ ...cargoForm, company_id: selectedCompany });
      toast.success('Cargo creado exitosamente');
      setCargoDialog(false);
      setCargoForm({ name: '', priority_level: 0 });
      loadData();
    } catch (error) {
      toast.error('Error al crear cargo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArea = async (id) => {
    if (!window.confirm('¿Eliminar esta área?')) return;
    try {
      await areasAPI.delete(id);
      toast.success('Área eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar área');
    }
  };

  const handleDeleteTeam = async (id) => {
    if (!window.confirm('¿Eliminar este equipo?')) return;
    try {
      await teamsAPI.delete(id);
      toast.success('Equipo eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar equipo');
    }
  };

  const handleDeleteCargo = async (id) => {
    if (!window.confirm('¿Eliminar este cargo?')) return;
    try {
      await cargosAPI.delete(id);
      toast.success('Cargo eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar cargo');
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Equipos, Áreas y Cargos
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gestiona la estructura organizacional
            </p>
          </div>
          {user?.role === 'superadmin' && companies.length > 0 && (
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[200px] rounded-sm" data-testid="company-selector">
                <SelectValue placeholder="Seleccionar empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Tabs defaultValue="areas">
          <TabsList className="mb-4">
            <TabsTrigger value="areas">Áreas</TabsTrigger>
            <TabsTrigger value="teams">Equipos</TabsTrigger>
            <TabsTrigger value="cargos">Cargos</TabsTrigger>
          </TabsList>

          {/* Areas Tab */}
          <TabsContent value="areas">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setAreaDialog(true)} className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" data-testid="add-area-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Área
              </Button>
            </div>
            {areas.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="flex flex-col items-center justify-center h-48 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay áreas registradas</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {areas.map((area) => (
                  <Card key={area.id} className="border border-slate-200 card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-indigo-600" />
                          </div>
                          <CardTitle className="text-lg">{area.name}</CardTitle>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteArea(area.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    {area.description && (
                      <CardContent>
                        <p className="text-sm text-slate-500">{area.description}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => { setTeamForm({ ...teamForm, area_id: areas[0]?.id || '' }); setTeamDialog(true); }} 
                className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                disabled={areas.length === 0}
                data-testid="add-team-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Equipo
              </Button>
            </div>
            {teams.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="flex flex-col items-center justify-center h-48 text-center">
                  <UserCog className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay equipos registrados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((team) => (
                  <Card key={team.id} className="border border-slate-200 card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-50 rounded-sm flex items-center justify-center">
                            <UserCog className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{team.name}</CardTitle>
                            <p className="text-xs text-slate-500">Coincidencia mín: {team.min_coincidence_days} día(s)</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTeam(team.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Cargos Tab */}
          <TabsContent value="cargos">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setCargoDialog(true)} className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" data-testid="add-cargo-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cargo
              </Button>
            </div>
            {cargos.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="flex flex-col items-center justify-center h-48 text-center">
                  <Award className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay cargos registrados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cargos.map((cargo) => (
                  <Card key={cargo.id} className="border border-slate-200 card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-50 rounded-sm flex items-center justify-center">
                            <Award className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{cargo.name}</CardTitle>
                            <p className="text-xs text-slate-500">Prioridad: {cargo.priority_level}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCargo(cargo.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Area Dialog */}
        <Dialog open={areaDialog} onOpenChange={setAreaDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nueva Área</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateArea} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del área</Label>
                <Input value={areaForm.name} onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })} required className="rounded-sm" data-testid="area-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input value={areaForm.description} onChange={(e) => setAreaForm({ ...areaForm, description: e.target.value })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAreaDialog(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-area-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Team Dialog */}
        <Dialog open={teamDialog} onOpenChange={setTeamDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nuevo Equipo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div className="space-y-2">
                <Label>Área</Label>
                <Select value={teamForm.area_id} onValueChange={(value) => setTeamForm({ ...teamForm, area_id: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="team-area-select">
                    <SelectValue placeholder="Seleccionar área" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre del equipo</Label>
                <Input value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} required className="rounded-sm" data-testid="team-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Días mínimos de coincidencia</Label>
                <Input type="number" min="0" value={teamForm.min_coincidence_days} onChange={(e) => setTeamForm({ ...teamForm, min_coincidence_days: parseInt(e.target.value) || 0 })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTeamDialog(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-team-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cargo Dialog */}
        <Dialog open={cargoDialog} onOpenChange={setCargoDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nuevo Cargo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCargo} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del cargo</Label>
                <Input value={cargoForm.name} onChange={(e) => setCargoForm({ ...cargoForm, name: e.target.value })} required className="rounded-sm" data-testid="cargo-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Nivel de prioridad (mayor = más prioridad)</Label>
                <Input type="number" min="0" value={cargoForm.priority_level} onChange={(e) => setCargoForm({ ...cargoForm, priority_level: parseInt(e.target.value) || 0 })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCargoDialog(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-cargo-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TeamsAreasPage;
